import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, Play, Sparkles, Check } from "lucide-react";
import { HeroGraphicCarousel } from "./HeroGraphicCarousel";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const rotateX = useTransform(sy, [-0.5, 0.5], [5, -5]);
  const rotateY = useTransform(sx, [-0.5, 0.5], [-7, 7]);

  const onMouseMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="relative overflow-hidden bg-gradient-to-b from-nova-mist via-white to-white"
    >
      <div className="absolute inset-0 bg-grid-navy [mask-image:radial-gradient(ellipse_70%_60%_at_50%_20%,black,transparent)]" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-nova-bluelight/50 blur-3xl" />
      <div className="absolute top-1/3 -left-48 w-[420px] h-[420px] rounded-full bg-nova-mist blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-36 pb-24 lg:pt-44 lg:pb-32">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-12 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-nova-blue/20 bg-white px-4 py-1.5 shadow-soft"
              data-testid="hero-eyebrow"
            >
              <Sparkles size={13} className="text-nova-blue" strokeWidth={1.8} />
              <span className="text-[11px] font-bold tracking-[0.22em] uppercase text-nova-blue">
                AI-Powered Business Operations
              </span>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="mt-7 text-5xl sm:text-6xl lg:text-[4.4rem] font-extrabold tracking-tighter leading-[1.02] text-nova-navy"
            >
              <MaskedLine delay={0.3}>Run Your Business.</MaskedLine>
              <MaskedLine delay={0.45} className="text-nova-blue">
                Nova Handles the Rest.
              </MaskedLine>
            </h1>

            <motion.p
              data-testid="hero-subcopy"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
              className="mt-7 text-base lg:text-lg text-nova-slate leading-relaxed max-w-lg"
            >
              Connect the tools you already use. Add the ones you do not. Nova
              brings your business operations together and helps handle the work
              behind the scenes.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Link
                  to="/request-demo"
                  data-testid="hero-request-demo-btn"
                  className="inline-flex items-center gap-2.5 rounded-full bg-nova-blue px-7 py-3.5 text-sm font-semibold text-white shadow-[0_12px_32px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300"
                >
                  Request Demo
                  <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </motion.div>
              <motion.a
                href="#how-to-setup"
                data-testid="hero-see-how-btn"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="inline-flex items-center gap-2.5 rounded-full border border-slate-300 bg-white px-7 py-3.5 text-sm font-semibold text-nova-navy hover:border-nova-blue/40 hover:bg-nova-mist transition-colors duration-300"
              >
                <Play size={14} strokeWidth={2} className="text-nova-blue" />
                See How Nova Works
              </motion.a>
            </motion.div>

            <motion.p
              data-testid="hero-trust-line"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.15 }}
              className="mt-8 flex items-center gap-2 text-sm text-nova-muted"
            >
              <Check size={15} className="text-nova-pine" strokeWidth={2.2} />
              Built for small and growing businesses.
            </motion.p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.55, ease: EASE }}
            style={{ perspective: 1200 }}
            className="relative pb-8"
          >
            <motion.div
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              <HeroGraphicCarousel />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
